import { useNavigate } from 'react-router-dom'
import { useTranslation } from '../../i18n'
import { useAuth } from '../../contexts/AuthContext'
import type { TrainingSetDetailsResponse } from '../../types/api'
import { formatShortDate } from '../../utils/dateTranslations'
import { Avatar } from '../ui'

interface SingleTrainingSetWidgetProps {
  trainingSetDetails: TrainingSetDetailsResponse
}

export default function SingleTrainingSetWidget({ trainingSetDetails }: SingleTrainingSetWidgetProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { user } = useAuth()

  const getCoachName = () => {
    if (!trainingSetDetails?.relatedUser) return t.trainingSetDetail.relatedUser.coachLabel
    const { firstName, lastName, username } = trainingSetDetails.relatedUser
    if (firstName && lastName) return `${firstName} ${lastName}`
    if (firstName) return firstName
    if (username) return `@${username}`
    return t.trainingSetDetail.relatedUser.coachLabel
  }

  const handleClick = () => {
    if (user?.id && trainingSetDetails.trainingSet.id) {
      navigate(`/trainee/${user.id}/set/${trainingSetDetails.trainingSet.id}`)
    }
  }

  const stats = trainingSetDetails.stats
  const totalCounted = stats
    ? stats.completedCount + stats.totalFiredCount + stats.futureCount
    : 0
  const completedPct = totalCounted > 0 ? (stats.completedCount / totalCounted) * 100 : 0
  const firedPct = totalCounted > 0 ? (stats.totalFiredCount / totalCounted) * 100 : 0
  const futurePct = totalCounted > 0 ? (stats.futureCount / totalCounted) * 100 : 0
  const markerPosition = completedPct + firedPct
  const hasMarker = futurePct > 0 && (completedPct > 0 || firedPct > 0)
  const donePct = Math.round(completedPct + firedPct)
  // Frosted glass on blue hero: bright done, softer mid/future, bordered track
  const segDone = 'rgba(255,255,255,0.98)'
  const segFired = 'rgba(255,255,255,0.48)'
  const segFuture = 'rgba(255,255,255,0.18)'
  const segTrack = 'rgba(255,255,255,0.12)'
  const markerColor = 'rgba(255,255,255,0.98)'
  const futureLabelColor = 'rgba(255,255,255,0.8)'
  const labelOnBright = 'rgba(0,70,110,0.92)'

  return (
    <div
      data-tour="tour-trainee-training-set"
      className="mb-6 rounded-2xl cursor-pointer transition-all active:scale-[0.98] relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #005f8f 0%, #0088cc 55%, #3ba8dc 100%)',
        boxShadow: '0 8px 32px rgba(0, 136, 204, 0.35)',
      }}
      onClick={handleClick}
    >
      {/* Decorative circles */}
      <div
        className="absolute pointer-events-none"
        style={{
          width: 160,
          height: 160,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.06)',
          top: -40,
          right: -40,
        }}
      />
      <div
        className="absolute pointer-events-none"
        style={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.05)',
          bottom: -20,
          right: 60,
        }}
      />

      <div className="relative p-5">
        {/* Header row: context label + arrow */}
        <div className="flex items-center justify-between mb-4">
          <span
            className="text-xs font-semibold tracking-widest uppercase"
            style={{ color: 'rgba(255,255,255,0.65)' }}
          >
            {t.traineeDashboard.trainingSetWidget.title}
          </span>
          <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 18 }}>→</span>
        </div>

        {/* Coach row */}
        <div className="flex items-center gap-3 mb-5">
          <div
            style={{
              padding: 2,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.25)',
              flexShrink: 0,
            }}
          >
            <Avatar
              photoUrl={trainingSetDetails.relatedUser?.photoUrl}
              name={getCoachName()}
              size="md"
              fallbackBg="rgba(255,255,255,0.2)"
            />
          </div>
          <div>
            <div
              className="text-xs font-medium mb-0.5"
              style={{ color: 'rgba(255,255,255,0.65)' }}
            >
              {t.trainingSetDetail.relatedUser.coachLabel}
            </div>
            <div className="text-lg font-bold" style={{ color: 'white' }}>
              {getCoachName()}
            </div>
          </div>
        </div>

        {/* Dates row */}
        <div className="flex items-center gap-3 mb-5">
          <div className="flex-1">
            <div className="text-xs mb-1" style={{ color: 'rgba(255,255,255,0.6)' }}>
              {t.traineeDashboard.trainingSetWidget.startDate}
            </div>
            <div className="text-sm font-semibold" style={{ color: 'white' }}>
              {formatShortDate(new Date(trainingSetDetails.trainingSet.startDate), t)}
            </div>
          </div>
          <div
            style={{
              width: 1,
              height: 28,
              background: 'rgba(255,255,255,0.2)',
              flexShrink: 0,
            }}
          />
          <div className="flex-1">
            <div className="text-xs mb-1" style={{ color: 'rgba(255,255,255,0.6)' }}>
              {t.traineeDashboard.trainingSetWidget.endDate}
            </div>
            <div className="text-sm font-semibold" style={{ color: 'white' }}>
              {formatShortDate(new Date(trainingSetDetails.trainingSet.calculatedEndDate), t)}
            </div>
          </div>
        </div>

        {/* Progress section */}
        {stats && (
          <div>
            {/* Progress label + percentage */}
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold" style={{ color: 'rgba(255,255,255,0.75)' }}>
                {t.trainingSetDetail.progress.title}
              </span>
              <span className="text-xs font-bold" style={{ color: 'rgba(255,255,255,0.9)' }}>
                {donePct}%
              </span>
            </div>

            {/* Progress bar — frosted glass on blue hero */}
            <div
              className="rounded-full overflow-visible relative"
              style={{
                height: 16,
                backgroundColor: segTrack,
                border: '1px solid rgba(255,255,255,0.35)',
                boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.2), 0 1px 2px rgba(0,0,0,0.12)',
                backdropFilter: 'blur(6px)',
              }}
            >
              <div className="h-full flex rounded-full overflow-hidden">
                {completedPct > 0 && (
                  <div
                    className="h-full transition-all duration-500 flex items-center justify-center text-xs font-bold"
                    style={{
                      width: `${completedPct}%`,
                      background: `linear-gradient(180deg, ${segDone} 0%, rgba(255,255,255,0.88) 100%)`,
                      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.65)',
                      color: labelOnBright,
                      minWidth: '2%',
                    }}
                  >
                    {completedPct > 12 && stats.completedCount}
                  </div>
                )}
                {firedPct > 0 && (
                  <div
                    className="h-full transition-all duration-500 flex items-center justify-center text-xs font-bold"
                    style={{
                      width: `${firedPct}%`,
                      backgroundColor: segFired,
                      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.25)',
                      color: labelOnBright,
                      minWidth: '2%',
                    }}
                  >
                    {firedPct > 12 && stats.totalFiredCount}
                  </div>
                )}
                {futurePct > 0 && (
                  <div
                    className="h-full transition-all duration-500 flex items-center justify-center text-xs font-bold"
                    style={{
                      width: `${futurePct}%`,
                      backgroundColor: segFuture,
                      color: futureLabelColor,
                      minWidth: '2%',
                    }}
                  >
                    {futurePct > 12 && stats.futureCount}
                  </div>
                )}
              </div>

              {/* You-are-here marker */}
              {hasMarker && (
                <div
                  className="absolute"
                  style={{
                    left: `${markerPosition}%`,
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 10,
                  }}
                >
                  <div
                    className="absolute"
                    style={{
                      width: 2,
                      height: 22,
                      backgroundColor: markerColor,
                      top: -11,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      boxShadow: '0 0 8px rgba(255,255,255,0.65)',
                    }}
                  />
                  <div
                    className="absolute flex items-center justify-center"
                    style={{
                      top: -20,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255,255,255,0.95)',
                      border: '1.5px solid rgba(255,255,255,0.85)',
                      boxShadow: '0 2px 10px rgba(0,0,0,0.2), 0 0 0 1px rgba(255,255,255,0.25)',
                      fontSize: 12,
                      lineHeight: 1,
                    }}
                  >
                    ⚡
                  </div>
                </div>
              )}
            </div>

            {/* Legend — compact horizontal chips */}
            <div className="flex flex-wrap gap-2 mt-3">
              <div
                className="flex items-center gap-1.5 text-xs font-medium"
                style={{ color: 'rgba(255,255,255,0.7)' }}
              >
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 2,
                    backgroundColor: segTrack,
                    border: '1px solid rgba(255,255,255,0.35)',
                    flexShrink: 0,
                  }}
                />
                {t.trainingSetDetail.progress.planned}: {stats.plannedCount}
              </div>
              <div
                className="flex items-center gap-1.5 text-xs font-medium"
                style={{ color: 'rgba(255,255,255,0.7)' }}
              >
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 2,
                    backgroundColor: segDone,
                    flexShrink: 0,
                  }}
                />
                {t.trainingSetDetail.progress.completed}: {stats.completedCount}
              </div>
              {stats.totalFiredCount > 0 && (
                <div
                  className="flex items-center gap-1.5 text-xs font-medium"
                  style={{ color: 'rgba(255,255,255,0.7)' }}
                >
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 2,
                      backgroundColor: segFired,
                      flexShrink: 0,
                    }}
                  />
                  {t.trainingSetDetail.progress.fired}: {stats.totalFiredCount}
                </div>
              )}
              <div
                className="flex items-center gap-1.5 text-xs font-medium"
                style={{ color: 'rgba(255,255,255,0.7)' }}
              >
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 2,
                    backgroundColor: segFuture,
                    flexShrink: 0,
                  }}
                />
                {t.trainingSetDetail.progress.remaining}: {stats.futureCount}
              </div>
            </div>

            {/* Skipped/cancelled — below a subtle divider */}
            {((stats.skippedCount ?? 0) + (stats.cancelledCount ?? 0)) > 0 && (
              <div
                className="flex items-center gap-1.5 text-xs mt-3 pt-3"
                style={{
                  borderTop: '1px solid rgba(255,255,255,0.15)',
                  color: 'rgba(255,255,255,0.5)',
                }}
              >
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 2,
                    backgroundColor: 'rgba(255,255,255,0.25)',
                    flexShrink: 0,
                  }}
                />
                {t.trainingSetDetail.progress.skippedCancelled}:{' '}
                {(stats.skippedCount ?? 0) + (stats.cancelledCount ?? 0)}
                <span style={{ marginLeft: 4, color: 'rgba(255,255,255,0.35)' }}>
                  ({t.trainingSetDetail.progress.notCounted})
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
